import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Alert, Box, Typography } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import type { EditorJsData } from '@/types'
import {
  EDITOR_JS_ROW_HEIGHT_PX,
  editorJsLayoutSx,
  editorJsThemeSx,
  type EditorJsLayout,
} from './editorJsTheme'

export type EditorJsFieldProps = {
  value: EditorJsData
  onChange: (next: EditorJsData) => void
  readOnly?: boolean
  /**
   * Расширенный набор блоков: цитата, таблица, разделитель, предупреждение.
   *
   * Поадресно, а не глобально: у новостей лэндинга свой белый список типов на
   * сервере и свой рендер на сайте, а домашку рендерит ещё и мобильное
   * приложение собственным кодом — незнакомый тип там просто не отрисуется.
   */
  extendedBlocks?: boolean
  /**
   * Фон области редактора в режиме только чтения: `disabled` — серый (по умолчанию),
   * `paper` — как у редактируемого поля (белый в светлой теме).
   */
  readOnlySurface?: 'disabled' | 'paper'
  /**
   * Вёрстка в режиме только чтения.
   *
   * `content` (по умолчанию) — просмотр материала: высота по содержимому,
   * текст на всю ширину, без пустой зоны под последним блоком.
   *
   * `field` — поле, которое блокируется на время (заметки урока, пока пишет
   * другой участник; домашка во время сохранения): размеры и отступы те же,
   * что у редактируемого поля, и переключение режима не двигает вёрстку.
   */
  readOnlyLayout?: EditorJsLayout
  label?: string
  /** Минимальная высота редактируемого поля в строках */
  rows?: number
  hideLabel?: boolean
  containerSx?: SxProps<Theme>
  holderSx?: SxProps<Theme>
  /**
   * Задержка перед созданием Editor.js (мс). Для модалок и скрытых контейнеров
   * иногда нужно 50–100 мс после появления в DOM.
   */
  initDelayMs?: number
  /** Фокус внутри редактора (capture), для блокировок по сокетам */
  onFocus?: () => void
  /** Потеря фокуса с области редактора */
  onBlur?: () => void
  /**
   * Загрузчик изображений: включает image-блок (@editorjs/image).
   * Не задан — набор инструментов прежний (заметки урока, ДЗ не меняются).
   */
  imageUploader?: (file: File) => Promise<{ url: string; path?: string }>
}

function normalizeEditorData(value: EditorJsData): EditorJsData {
  // If API provided an explicit blocks array (even empty), don't "help" Editor.js by injecting blocks.
  if (Array.isArray(value?.blocks)) {
    if (value.blocks.length > 0) return value
    // If blocks is explicitly empty, we still pass a single empty paragraph.
    // This makes initial rendering deterministic and prevents "double empty paragraph" states.
    return {
      time: value?.time,
      version: value?.version ?? '2.0.0',
      blocks: [
        {
          type: 'paragraph',
          data: { text: '' },
        },
      ],
    }
  }

  // For non-standard/undefined payloads, inject a minimal paragraph so the editor always has a place to render.
  return {
    time: value?.time,
    version: value?.version ?? '2.0.0',
    blocks: [
      {
        type: 'paragraph',
        data: { text: '' },
      },
    ],
  }
}

type EditorJsInstance = import('@editorjs/editorjs').default

/** Значение поля и его ключ сравнения (JSON нормализованных данных) */
type EditorValue = { data: EditorJsData; key: string }

/**
 * Показать в редакторе значение, если оно разошлось с уже показанным.
 *
 * `shownKeyRef` — ключ того, что сейчас в редакторе: его же обновляет и
 * сохранение правки, поэтому своё же изменение, вернувшееся пропом, заново не
 * рендерится.
 */
async function renderIfChanged(
  editor: EditorJsInstance,
  value: EditorValue,
  shownKeyRef: { current: string },
): Promise<void> {
  if (shownKeyRef.current === value.key) return
  shownKeyRef.current = value.key

  try {
    // Повторный render без очистки у Editor.js иногда дописывает блоки к старым
    await editor.clear()
  } catch {
    // render ниже всё равно попробует
  }

  try {
    await editor.render(value.data)
  } catch {
    // Битые данные: остаётся то, что уже показано, интерфейс не ломается
  }
}

async function toggleReadOnly(editor: EditorJsInstance, readOnly: boolean): Promise<void> {
  const reader = editor as unknown as { readOnly?: { toggle?: (value: boolean) => Promise<boolean> | boolean } }

  try {
    // Переключение перерисовывает блоки — дожидаемся, прежде чем рендерить поверх
    await reader.readOnly?.toggle?.(readOnly)
  } catch (e: unknown) {
    console.error('[EditorJsField] readOnly toggle failed', e)
  }
}

/**
 * Поставить операцию с редактором в очередь.
 *
 * Переключение режима чтения в Editor.js асинхронно: оно сохраняет текущие
 * блоки, очищает редактор и рисует их заново. Рендер нового значения,
 * запущенный параллельно, успевал отработать между этими шагами — и
 * переключение возвращало поверх старые блоки. Поэтому toggle и render идут
 * строго друг за другом. Операция для уже заменённого или уничтоженного
 * редактора пропускается.
 */
function enqueueEditorOp(
  queueRef: { current: Promise<void> },
  editorRef: { current: EditorJsInstance | null },
  editor: EditorJsInstance,
  op: () => Promise<void>,
): Promise<void> {
  const next = queueRef.current
    .then(() => (editorRef.current === editor ? op() : undefined))
    .catch(() => {})
  queueRef.current = next

  return next
}

function waitNextPaint(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve())
    })
  })
}

export function EditorJsField({
  value,
  onChange,
  readOnly,
  extendedBlocks = false,
  label,
  rows = 7,
  hideLabel,
  containerSx,
  holderSx,
  initDelayMs = 0,
  readOnlySurface = 'disabled',
  readOnlyLayout = 'content',
  onFocus,
  onBlur,
  imageUploader,
}: EditorJsFieldProps) {
  const { t } = useTranslation()
  const holderRef = useRef<HTMLDivElement | null>(null)
  /** Редактор, готовый к работе (после `isReady`): до этого его API ещё не подключён */
  const editorRef = useRef<EditorJsInstance | null>(null)
  const changeTimeoutRef = useRef<number | null>(null)
  /** Очередь toggle/render — см. enqueueEditorOp */
  const editorOpsRef = useRef<Promise<void>>(Promise.resolve())
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const imageUploaderRef = useRef(imageUploader)
  imageUploaderRef.current = imageUploader

  const [initError, setInitError] = useState<string | null>(null)

  const normalizedValue = useMemo(() => normalizeEditorData(value), [value])
  const valueKey = useMemo(() => JSON.stringify(normalizedValue), [normalizedValue])
  const lastValueKeyRef = useRef<string>(valueKey)

  const effectiveReadOnly = Boolean(readOnly)
  const layout: EditorJsLayout = effectiveReadOnly ? readOnlyLayout : 'field'

  // Актуальные данные и режим для асинхронного создания редактора. Между
  // монтированием и готовностью Editor.js родитель успевает их поменять:
  // диалог правки копии темы подставляет тело эффектом уже после открытия, а
  // заметка урока блокируется по сокету. Замыкание эффекта создания помнит
  // значения первого рендера — и учитель видел пустую теорию вместо текста.
  const latestValueRef = useRef<EditorValue>({ data: normalizedValue, key: valueKey })
  latestValueRef.current = { data: normalizedValue, key: valueKey }
  const readOnlyRef = useRef(effectiveReadOnly)
  readOnlyRef.current = effectiveReadOnly
  const computedLabel = label ?? 'Editor.js'
  const shouldShowLabel = !hideLabel && Boolean(computedLabel)

  const saveAndEmit = async () => {
    const editor = editorRef.current
    if (!editor) return
    const saved = await editor.save()
    const nextData = saved as EditorJsData
    const normalizedSaved = normalizeEditorData(nextData)
    lastValueKeyRef.current = JSON.stringify(normalizedSaved)
    onChangeRef.current(normalizedSaved)
  }

  useLayoutEffect(() => {
    let destroyed = false
    let delayTimer: number | null = null
    /** Снимок DOM-узла для cleanup (не полагаться на holderRef.current в teardown). */
    let holderForCleanup: HTMLDivElement | null = null
    /** Созданный, но, возможно, ещё не готовый редактор — его тоже надо уничтожить */
    let createdEditor: EditorJsInstance | null = null

    const run = async () => {
      setInitError(null)
      let holderElement = holderRef.current
      if (!holderElement) {
        await waitNextPaint()
        holderElement = holderRef.current
      }
      if (!holderElement) {
        setInitError('Editor container not ready')
        return
      }

      holderForCleanup = holderElement

      if (initDelayMs > 0) {
        await new Promise<void>((r) => {
          delayTimer = window.setTimeout(() => r(), initDelayMs)
        })
      }
      if (destroyed) return
      await waitNextPaint()
      if (destroyed || !holderRef.current) return

      holderElement.innerHTML = ''

      try {
        const [editorMod, headerMod, paragraphMod, listMod] = await Promise.all([
          import('@editorjs/editorjs'),
          import('@editorjs/header'),
          import('@editorjs/paragraph'),
          import('@editorjs/list'),
        ])
        const EditorJS = (editorMod.default ?? editorMod) as typeof import('@editorjs/editorjs').default
        const Header = (headerMod as { default?: unknown }).default ?? headerMod
        const Paragraph = (paragraphMod as { default?: unknown }).default ?? paragraphMod
        const List = (listMod as { default?: unknown }).default ?? listMod

        const tools: Record<string, unknown> = {
          header: Header,
          paragraph: Paragraph,
          list: List,
        }

        if (extendedBlocks) {
          const [quoteMod, tableMod, delimiterMod, warningMod] = await Promise.all([
            import('@editorjs/quote'),
            import('@editorjs/table'),
            import('@editorjs/delimiter'),
            import('@editorjs/warning'),
          ])
          const Quote = (quoteMod as { default?: unknown }).default ?? quoteMod
          const Table = (tableMod as { default?: unknown }).default ?? tableMod
          const Delimiter = (delimiterMod as { default?: unknown }).default ?? delimiterMod
          const Warning = (warningMod as { default?: unknown }).default ?? warningMod

          tools.quote = {
            class: Quote,
            config: {
              quotePlaceholder: t('editor.quotePlaceholder'),
              captionPlaceholder: t('editor.quoteCaption'),
            },
          }
          tools.table = { class: Table, inlineToolbar: true }
          tools.delimiter = Delimiter
          tools.warning = {
            class: Warning,
            config: {
              titlePlaceholder: t('editor.warningTitle'),
              messagePlaceholder: t('editor.warningMessage'),
            },
          }
        }

        const initialReadOnly = readOnlyRef.current

        // image-блок нужен и без загрузчика: в режиме чтения без него Editor.js
        // не знает тип блока и молча выбрасывает сохранённую картинку — ученик
        // видел бы теорию без иллюстраций, которые вставил учитель
        if (imageUploaderRef.current || initialReadOnly) {
          const imageMod = await import('@editorjs/image')
          const ImageTool = (imageMod as { default?: unknown }).default ?? imageMod
          tools.image = {
            class: ImageTool,
            config: {
              uploader: {
                uploadByFile: async (file: File) => {
                  const uploader = imageUploaderRef.current

                  // Читателю загружать нечем — молча ничего не делаем вместо
                  // падения инструмента
                  if (uploader === undefined) return { success: 0 }

                  return { success: 1, file: await uploader(file) }
                },
              },
            },
          }
        }

        if (destroyed || !holderRef.current) return

        const initialValue = latestValueRef.current
        const editor = new EditorJS({
          holder: holderElement,
          data: initialValue.data,
          readOnly: initialReadOnly,
          autofocus: false,
          // Без этого Editor.js держит под последним блоком 300px пустоты, и
          // в режиме чтения тоже. Зону клика у поля задаёт editorJsLayoutSx:
          // эта настройка читается только при создании, а режим чтения в
          // заметках урока переключается на лету.
          minHeight: 0,
          tools: tools as import('@editorjs/editorjs').EditorConfig['tools'],
          onChange: () => {
            if (readOnlyRef.current) return
            if (changeTimeoutRef.current != null) window.clearTimeout(changeTimeoutRef.current)
            changeTimeoutRef.current = window.setTimeout(() => {
              saveAndEmit().catch(() => {})
            }, 350)
          },
        })
        createdEditor = editor

        await editor.isReady
        if (destroyed) {
          try {
            editor.destroy()
          } catch {
            /* noop */
          }
          return
        }
        editorRef.current = editor
        lastValueKeyRef.current = initialValue.key

        // Пока Editor.js поднимался, эффекты режима и данных редактора не
        // видели и изменения пропустили — догоняем. Через общую очередь: с этого
        // момента эффекты уже видят редактор и ставят свои операции следом
        await enqueueEditorOp(editorOpsRef, editorRef, editor, async () => {
          if (readOnlyRef.current !== initialReadOnly) {
            await toggleReadOnly(editor, readOnlyRef.current)
          }
          await renderIfChanged(editor, latestValueRef.current, lastValueKeyRef)
        })
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e)
        console.error('[EditorJsField] init failed', e)
        setInitError(msg || 'Editor.js init failed')
      }
    }

    void run()

    return () => {
      destroyed = true
      if (delayTimer != null) window.clearTimeout(delayTimer)
      if (changeTimeoutRef.current != null) window.clearTimeout(changeTimeoutRef.current)
      const ed = editorRef.current ?? createdEditor
      editorRef.current = null
      const holderElement = holderForCleanup
      const cleanup = async () => {
        if (!ed) {
          if (holderElement) holderElement.innerHTML = ''
          return
        }
        try {
          await ed.isReady
        } catch {
          /* noop */
        }
        try {
          ed.destroy()
        } catch {
          /* noop */
        } finally {
          if (holderElement) holderElement.innerHTML = ''
        }
      }
      void cleanup()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initDelayMs])

  // Режим чтения переключается на лету. Редактор ещё не готов — режим
  // подхватит его создание
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return

    void enqueueEditorOp(editorOpsRef, editorRef, editor, () => toggleReadOnly(editor, effectiveReadOnly))
  }, [effectiveReadOnly])

  // Новое значение от родителя. Редактор ещё не готов — значение подхватит
  // его создание
  useEffect(() => {
    const editor = editorRef.current
    if (!editor) return

    // В очереди после переключения режима: render идёт уже поверх
    // перерисовки, которую сделал toggle, и показывает последнее значение
    void enqueueEditorOp(editorOpsRef, editorRef, editor, () =>
      renderIfChanged(editor, { data: normalizedValue, key: valueKey }, lastValueKeyRef),
    )
  }, [valueKey, normalizedValue])

  return (
    <Box
      sx={{ width: '100%', ...(containerSx ?? {}) }}
      onFocusCapture={() => onFocus?.()}
      onBlurCapture={(e) => {
        const next = e.relatedTarget as Node | null
        if (next && e.currentTarget.contains(next)) return
        onBlur?.()
      }}
    >
      {initError && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {initError}
        </Alert>
      )}
      {shouldShowLabel && (
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          {computedLabel}
        </Typography>
      )}
      <Box
        ref={holderRef}
        // Массивная форма sx, а не спред: `holderSx` приходит как SxProps и
        // может быть функцией или массивом — спред такого в объект ломает типы.
        sx={[
          (theme) => ({
            width: '100%',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            // Просмотр — по содержимому: пустота под короткой теорией ни к чему
            minHeight: layout === 'field' ? rows * EDITOR_JS_ROW_HEIGHT_PX : undefined,
            padding: 1,
            backgroundColor: effectiveReadOnly
              ? readOnlySurface === 'paper'
                ? 'background.paper'
                : 'action.disabledBackground'
              : 'background.paper',
            ...editorJsLayoutSx(layout),
            // Своей тёмной темы у Editor.js нет — переопределения живут
            // отдельным модулем и применяются только в тёмной теме.
            ...editorJsThemeSx(theme),
          }),
          ...(Array.isArray(holderSx) ? holderSx : [holderSx]),
        ]}
      />
    </Box>
  )
}

